import { useQuery } from '@apollo/client';
import { Button, MenuItem } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { BaseDialog } from '../../../../base/components/BaseDialog';
import { Flex } from '../../../../base/components/Flex';
import FormikForm from '../../../../base/components/form/FormikForm';
import FormikSelect from '../../../../base/components/form/FormikSelect';
import { FormikSelectMultipleWithContext } from '../../../../base/components/form/FormikSelectMultipleWithContext';
import FormikTextField from '../../../../base/components/form/FormikTextField';
import {
  CompanyFilesDocument,
  CompanyFileType,
  CompanyQrLoginType,
  CompanyQrScenesDocument,
  CompanyQrsQuery,
  EditCompanyQrDocument,
  EditCompanyQrFileInput,
  GetVrDevicesDocument,
} from '../../../../graphql/graphql-operations';
import { useMutationWithSnackbar } from '../../../../hooks/useMutationWithSnackbar';
import {
  TFunctionPrefixed,
  useTranslationPrefix,
} from '../../../../hooks/useTranslationPrefix';

interface FormData {
  vrDeviceId: string;
  backupVrDeviceId: string;
  qrSceneFilter: string[];
  image_1: string;
  image_2: string;
  additionalParams: string;
  loginType: CompanyQrLoginType;
}

interface Props {
  qrCode: CompanyQrsQuery['companyQrs'][0];
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CompanyQrDialog = ({ open, qrCode, onClose, onSuccess }: Props) => {
  const { _t, t } = useTranslationPrefix('CompanyQrsPage');

  const { data: devicesData } = useQuery(GetVrDevicesDocument, {
    variables: {
      companyIdOptionalInput: {
        companyId: qrCode.company.id,
      },
    },
  });
  const devices = devicesData?.vrDevices || [];

  const { data: imagesData } = useQuery(CompanyFilesDocument, {
    variables: {
      input: {
        companyId: qrCode.company.id,
        type: CompanyFileType.IMAGE,
      },
    },
  });
  const images = imagesData?.companyFiles || [];

  const { data: qrScenesData } = useQuery(CompanyQrScenesDocument, {
    variables: {
      input: {
        companyId: qrCode.company.id,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const qrScenes = qrScenesData?.companyQrScenes || [];

  const [editMutation] = useMutationWithSnackbar(
    'CompanyQrsPage.editMutation',
    EditCompanyQrDocument,
    {
      onCompleted: () => {
        onSuccess?.();
      },
    },
  );

  const handleSubmit = async (
    values: FormData,
    helpers: FormikHelpers<FormData>,
  ) => {
    const files: EditCompanyQrFileInput[] = [];
    if (values.image_1) {
      files.push({ paramName: 'image_1', fileId: values.image_1 });
    }
    if (values.image_2) {
      files.push({ paramName: 'image_2', fileId: values.image_2 });
    }
    await editMutation({
      variables: {
        input: {
          id: qrCode.id,
          vrDeviceId: values.vrDeviceId,
          backupVrDeviceId: values.backupVrDeviceId || null,
          qrSceneFilter: values.qrSceneFilter || [],
          additionalParams: values.additionalParams,
          files: files,
          loginType: values.loginType,
        },
      },
    });
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={_t('dialog.editTitle', 'Edit QR code')}
      disableEnforceFocus
    >
      <>
        <Formik<FormData>
          initialValues={{
            vrDeviceId: qrCode.vrDevice.id,
            backupVrDeviceId: qrCode.backupVrDevice?.id || '',
            qrSceneFilter: qrCode.qrSceneFilter || [],
            additionalParams: qrCode.additionalParams,
            image_1:
              qrCode.files.find((file) => file.paramName === 'image_1')?.file
                .id || '',
            image_2:
              qrCode.files.find((file) => file.paramName === 'image_2')?.file
                .id || '',
            loginType: qrCode.loginType || CompanyQrLoginType.EMAIL,
          }}
          onSubmit={handleSubmit}
          enableReinitialize
          validationSchema={getValidationSchema(_t)}
        >
          {({ isSubmitting }) => (
            <FormikForm>
              <Flex flexDirection="column" gap={3}>
                <FormikSelect
                  fullWidth
                  name="vrDeviceId"
                  label={_t('dialog.vrDeviceId', 'VR Device')}
                >
                  {devices?.map((device) => (
                    <MenuItem key={device.id} value={device.id}>
                      {device.name}
                    </MenuItem>
                  ))}
                </FormikSelect>

                <FormikSelect
                  fullWidth
                  name="backupVrDeviceId"
                  label={_t('dialog.backupVrDeviceId', 'Backup VR Device')}
                >
                  <MenuItem value={''}>{_t('noDevice', 'No device')}</MenuItem>
                  {devices?.map((device) => (
                    <MenuItem key={device.id} value={device.id}>
                      {device.name}
                    </MenuItem>
                  ))}
                </FormikSelect>
                <FormikSelectMultipleWithContext
                  name="qrSceneFilter"
                  fullWidth
                  label={_t('dialog.qrSceneFilter', 'Qr Scenes')}
                  multiple
                  color="primary"
                  sx={{ width: '100%' }}
                  options={qrScenes.map((scene) => ({
                    id: scene.id,
                    name: scene.scene.name_t,
                  }))}
                />

                <FormikSelect
                  fullWidth
                  name="loginType"
                  label={_t('dialog.loginType', 'Login type')}
                >
                  <MenuItem value={CompanyQrLoginType.NOT_SET}>
                    {t(
                      `enum.CompanyQrLoginType.${CompanyQrLoginType.NOT_SET}`,
                      'Both',
                    )}
                  </MenuItem>
                  <MenuItem value={CompanyQrLoginType.ANNONYMOUS}>
                    {t(
                      `enum.CompanyQrLoginType.${CompanyQrLoginType.ANNONYMOUS}`,
                      'Annonymous',
                    )}
                  </MenuItem>
                  <MenuItem value={CompanyQrLoginType.EMAIL}>
                    {t(
                      `enum.CompanyQrLoginType.${CompanyQrLoginType.EMAIL}`,
                      'E-mail',
                    )}
                  </MenuItem>
                </FormikSelect>

                <FormikSelect fullWidth name="image_1" label="image_1">
                  <MenuItem value={''}>
                    {_t('noImage', 'Without image')}
                  </MenuItem>
                  {images?.map((image) => (
                    <MenuItem key={image.id} value={image.id}>
                      {image.name}
                    </MenuItem>
                  ))}
                </FormikSelect>

                <FormikSelect fullWidth name="image_2" label="image_2">
                  <MenuItem value={''}>
                    {_t('noImage', 'Without image')}
                  </MenuItem>

                  {images?.map((image) => (
                    <MenuItem key={image.id} value={image.id}>
                      {image.name}
                    </MenuItem>
                  ))}
                </FormikSelect>

                <FormikTextField
                  fullWidth
                  name="additionalParams"
                  label={_t(
                    'dialog.additionalParams',
                    'Additional params (json)',
                  )}
                  multiline
                  minRows={3}
                  maxRows={8}
                />

                <Flex
                  justifyContent="center"
                  mt={1}
                  gap={3}
                  flexDirection={{ xs: 'column-reverse', md: 'row' }}
                >
                  <Button
                    id="create-playlist-folder-cancel"
                    onClick={onClose}
                    color="primary"
                    variant="outlined"
                    disabled={isSubmitting}
                  >
                    {_t('dialog.cancel', 'Cancel')}
                  </Button>
                  <Button
                    id="create-playlist-folder-confirm"
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={isSubmitting}
                  >
                    {_t('dialog.submit', 'Save')}
                  </Button>
                </Flex>
              </Flex>
            </FormikForm>
          )}
        </Formik>
      </>
    </BaseDialog>
  );
};

export default CompanyQrDialog;

const getValidationSchema = (_t: TFunctionPrefixed) =>
  Yup.object({
    vrDeviceId: Yup.string().required(
      _t('dialog.vrDeviceIdRequired', 'Select VR Device'),
    ),
    additionalParams: Yup.string()
      .required(
        _t('dialog.additionalParamsRequired', 'Additional params are required'),
      )
      .test(
        'json',
        _t('dialog.additionalParamsInvalid', 'Invalid json'),
        (value) => {
          try {
            JSON.parse(value || '');
            return true;
          } catch (error) {
            return false;
          }
        },
      ),
  });
